import moment from "moment";
import { IAvailableSlot, IReservedSlot } from "./helper";

// "Every Mon-Fri, 10 AM to 1 PM, until March 20" as data, so the same pattern can
// be applied to the month on screen now and to later months when saving.
export interface Pattern {
  mode: "add" | "remove";
  days: number[]; // 0 = Sunday
  from: number; // first hour
  until: number; // end hour, exclusive
  startDay: string; // YYYY-MM-DD
  lastDay: string; // YYYY-MM-DD, inclusive
}

// The pattern applied to one (local) month of slots. Days already past, hours
// already passed today and hours that are booked are never added.
export const applyPattern = (
  slots: IAvailableSlot[],
  reserved: IReservedSlot[],
  p: Pattern,
  month0: number,
  year: number,
  now: moment.Moment = moment()
): { slots: IAvailableSlot[]; changed: number } => {
  const byDate = new Map<string, Set<number>>();
  slots.forEach((s) => byDate.set(s.date, new Set(s.time)));

  const startDay = moment(p.startDay, "YYYY-MM-DD");
  const lastDay = moment(p.lastDay, "YYYY-MM-DD");
  const today = now.clone().startOf("day");
  let changed = 0;

  for (let d = moment([year, month0, 1]); d.month() === month0; d.add(1, "day")) {
    if (d.isBefore(today) || d.isBefore(startDay, "day") || d.isAfter(lastDay, "day")) continue;
    if (!p.days.includes(d.day())) continue;
    const key = d.format("MM-DD");
    for (let h = p.from; h < p.until; h++) {
      if (p.mode === "add") {
        if (reserved.some((r) => r.date === key && +r.time === h)) continue;
        if (d.clone().hour(h).isBefore(now)) continue;
        const set = byDate.get(key) || new Set<number>();
        if (!set.has(h)) changed++;
        byDate.set(key, set.add(h));
      } else if (byDate.get(key)?.delete(h)) {
        changed++;
      }
    }
  }

  return {
    slots: Array.from(byDate.entries())
      .filter(([, set]) => set.size)
      .map(([date, set]) => ({ date, time: Array.from(set).sort((a, b) => a - b) })),
    changed,
  };
};

// The months after the viewed one that the pattern reaches, across year ends.
export const laterMonths = (p: Pattern, viewedMonth0: number, viewedYear: number): { month0: number; year: number }[] => {
  const last = moment(p.lastDay, "YYYY-MM-DD");
  const out: { month0: number; year: number }[] = [];
  for (let m = moment([viewedYear, viewedMonth0, 1]).add(1, "month"); !m.isAfter(last, "month"); m.add(1, "month")) {
    out.push({ month0: m.month(), year: m.year() });
  }
  return out;
};

// The furthest a fill may reach, so one press cannot turn into dozens of saves.
export const MAX_FILL_MONTHS = 12;
